'use client';

import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {
    detectBias,
    generateExplainableComparisons,
    getAnalysisHistory,
    getBertPredictionVisualization,
    getBertVisualization,
} from '@/services';
import { downloadFile, prepareMarkdown } from '@/utils';

import {
    BiasAnalysis,
    BiasAnalysisRecord,
    ComparisonExplanations,
    IBertMultiLabelData,
    IBertVisualizationData,
} from '@/types/common';
import { SAMPLE_BIASED_STATEMENTS } from '@/config/app';

export const MAX_CHARS = 500;

const DEFAULT_BIAS_ANALYSIS: BiasAnalysis = {
    biasedWords: [],
    labeledText: {},
    prediction: {},
};

const DEFAULT_VISUALIZATION_OUTPUT: IBertVisualizationData = {
    headView: null,
    bodyView: null,
};

interface BiasDetectionContextType {
    text: string;
    biasAnalysis: BiasAnalysis;
    isAnalyzing: boolean;
    isExplaining: boolean;
    progress: number;
    sensitivity: number;
    biasFilter: string;
    searchTerm: string;
    showHistory: boolean;
    showGuidedTour: boolean;
    showIntro: boolean;
    analysisHistory: BiasAnalysisRecord[];
    bertVisualizationOutput: IBertVisualizationData;
    bertMultiLabelOutput: IBertMultiLabelData | null;
    combinedExplanation: ComparisonExplanations | null;
    onDetectBias: () => Promise<void>;
    onExplainBias: () => Promise<void>;
    handleFileContent: (content: string) => void;
    handleTextChange: (newText: string) => void;
    handleReset: () => void;
    handleTest: () => void;
    setSensitivity: (value: number) => void;
    setBiasFilter: (value: string) => void;
    handleExport: () => void;
    setShowHistory: (show: boolean) => void;
    setShowGuidedTour: (show: boolean) => void;
    closeIntro: () => void;
    clearHistory: () => void;
}

const BiasDetectionContext = createContext<
    BiasDetectionContextType | undefined
>(undefined);

interface BiasDetectionProviderProps {
    children: ReactNode;
}

export const BiasDetectionProvider = ({
    children,
}: BiasDetectionProviderProps) => {
    const [text, setText] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isExplaining, setIsExplaining] = useState(false);
    const [progress, setProgress] = useState(0);
    const [sensitivity, setSensitivity] = useState(50);
    const [biasFilter, setBiasFilter] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [showGuidedTour, setShowGuidedTour] = useState(false);
    const [showIntro, setShowIntro] = useState(false);
    const [bertVisualizationOutput, setBertVisualizationOutput] =
        useState<IBertVisualizationData>(DEFAULT_VISUALIZATION_OUTPUT);
    const [bertMultiLabelOutput, setBertMultiLabelOutput] =
        useState<IBertMultiLabelData | null>(null);
    const [combinedExplanation, setCombinedExplanation] =
        useState<ComparisonExplanations | null>(null);
    const [biasAnalysis, setBiasAnalysis] = useState<BiasAnalysis>(
        DEFAULT_BIAS_ANALYSIS
    );
    const [analysisHistory, setAnalysisHistory] = useState<
        BiasAnalysisRecord[]
    >([]);

    useEffect(() => {
        const hasSeenIntro = sessionStorage.getItem('hasSeenIntro');
        setShowIntro(!hasSeenIntro);
    }, []);

    useEffect(() => {
        loadHistoryFromAPI();
    }, [isAnalyzing]);

    const loadHistoryFromAPI = async () => {
        try {
            const historyData = await getAnalysisHistory();
            setAnalysisHistory(historyData);
        } catch (error) {
            console.error('Failed to load history from API:', error);

            setAnalysisHistory([]);
        }
    };

    const fetchBertVisualization = async (
        text: string,
        sensitivity: number
    ) => {
        const bertVisualizationOutput = await getBertVisualization(
            text,
            sensitivity
        );
        setBertVisualizationOutput({
            headView: bertVisualizationOutput.head_view.data,
            bodyView: bertVisualizationOutput.model_view.data,
        });
    };

    const fetchBertPredictionVisualization = async (
        text: string,
        sensitivity: number
    ) => {
        const bertMultiLabelOutput = await getBertPredictionVisualization(
            text,
            sensitivity
        );
        setBertMultiLabelOutput(bertMultiLabelOutput);
    };

    const fetchExplainableComparisons = async (
        text: string,
        sensitivity: number
    ) => {
        const combinedExplanation = await generateExplainableComparisons(
            text,
            sensitivity,
            biasAnalysis?.prediction?.tokens || []
        );
        setCombinedExplanation(combinedExplanation);
    };

    const closeIntro = useCallback(() => {
        setShowIntro(false);
        sessionStorage.setItem('hasSeenIntro', 'true');
    }, []);

    const handleTextChange = useCallback((newText: string) => {
        if (newText.length <= MAX_CHARS) {
            setSearchTerm(newText);
        }
    }, []);

    const handleFileContent = useCallback((content: string) => {
        if (content.length <= MAX_CHARS) {
            setSearchTerm(content);
        }
    }, []);

    const handleReset = useCallback(() => {
        setText('');
        setSearchTerm('');
        setBiasAnalysis(DEFAULT_BIAS_ANALYSIS);
        setBertMultiLabelOutput(null);
        setBertVisualizationOutput(DEFAULT_VISUALIZATION_OUTPUT);
        setCombinedExplanation(null);
    }, []);

    const handleTest = useCallback(() => {
        handleReset();
        const randomBias =
            SAMPLE_BIASED_STATEMENTS[
                Math.floor(Math.random() * SAMPLE_BIASED_STATEMENTS.length)
            ];
        setSearchTerm(randomBias);
    }, [handleReset]);

    const clearHistory = useCallback(() => {
        setAnalysisHistory([]);

        setTimeout(() => loadHistoryFromAPI(), 500);
    }, []);

    const handleExport = useCallback(() => {
        if (!text) {
            console.warn('No text to export.');
            return;
        }

        const markdownContent = prepareMarkdown({
            currentText: text,
            normalizedSensitivity: sensitivity / 100,
            biasAnalysis,
        });

        downloadFile(markdownContent);
    }, [text, biasAnalysis, sensitivity]);

    const refreshHistoryAfterAnalysis = useCallback(async () => {
        await loadHistoryFromAPI();
    }, []);

    const onDetectBias = useCallback(async () => {
        const currentText = searchTerm.trim();
        if (!currentText) return;

        setIsAnalyzing(true);
        setProgress(50);

        try {
            const analysis = await detectBias(currentText, sensitivity / 100);
            setBiasAnalysis(analysis);
            setText(currentText);
            setProgress(100);
        } catch (error) {
            console.error('Error analyzing text:', error);
        } finally {
            setIsAnalyzing(false);
        }
    }, [searchTerm, sensitivity]);

    const onExplainBias = useCallback(async () => {
        const currentText = searchTerm.trim();
        if (!currentText) return;

        setIsExplaining(true);

        try {
            const normalizedSensitivity = sensitivity / 100;

            await fetchBertVisualization(currentText, normalizedSensitivity);

            await fetchBertPredictionVisualization(
                currentText,
                normalizedSensitivity
            );

            await fetchExplainableComparisons(
                currentText,
                normalizedSensitivity
            );

            await refreshHistoryAfterAnalysis();
        } catch (error) {
            console.error('Error explaining bias:', error);
        } finally {
            setIsExplaining(false);
        }
    }, [searchTerm, sensitivity, biasAnalysis]);

    const contextValue: BiasDetectionContextType = {
        text,
        biasAnalysis,
        isAnalyzing,
        isExplaining,
        progress,
        sensitivity,
        biasFilter,
        searchTerm,
        showHistory,
        showGuidedTour,
        showIntro,
        analysisHistory,
        bertVisualizationOutput,
        bertMultiLabelOutput,
        combinedExplanation,
        onDetectBias,
        onExplainBias,
        handleFileContent,
        handleTextChange,
        handleReset,
        handleTest,
        setSensitivity,
        setBiasFilter,
        handleExport,
        setShowHistory,
        setShowGuidedTour,
        closeIntro,
        clearHistory,
    };

    return (
        <BiasDetectionContext.Provider value={contextValue}>
            {children}
        </BiasDetectionContext.Provider>
    );
};

export const useBiasDetection = () => {
    const context = useContext(BiasDetectionContext);

    if (context === undefined) {
        throw new Error(
            'useBiasDetection must be used within a BiasDetectionProvider'
        );
    }

    return context;
};
